import {
	Badge,
	Button,
	Card,
	Flex,
	Group,
	SimpleGrid,
	Stack,
	Text,
	Title,
} from '@mantine/core'
import { IconRefresh } from '@tabler/icons-react'
import dayjs from 'dayjs'
import { MRT_ColumnDef } from 'mantine-react-table'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
	getGooglePlacesAutocompleteStats,
	getGoogleGeocodeStats,
	getGooglePlacesTextSearchStats,
} from 'src/apis/googleAPI'
import CustomDatePickerInput from 'src/components/CustomComponents/CustomDatePickerInput'
import CustomTooltip from 'src/components/CustomComponents/CustomTooltip'
import TableComponent from 'src/components/TableComponent'
import { useModal } from 'src/context/ModalContext'
import { color, formatDate, size } from 'src/variable/constant'

const RATE_FIELDS = [
	'google_avoid_rate',
	'google_call_rate',
	'estimated_saved_rate',
]
const MONEY_FIELDS = [
	'estimated_old_cost_usd',
	'estimated_new_cost_usd',
	'estimated_saved_usd',
]

const METRIC_LABELS = {
	request_total: 'Request total',
	served_by_redis: 'Served by Redis',
	served_by_db_exact: 'Served by DB exact',
	served_by_db_nearby: 'Served by DB nearby',
	served_by_db: 'Served by DB',
	served_by_google: 'Served by Google',
	served_by_error: 'Served by error',
	redis_hit: 'Redis hit',
	redis_miss: 'Redis miss',
	redis_error: 'Redis error',
	db_exact_hit: 'DB exact hit',
	db_exact_miss: 'DB exact miss',
	db_nearby_hit: 'DB nearby hit',
	db_nearby_miss: 'DB nearby miss',
	db_canonical_hit: 'DB canonical hit',
	db_address_value_hit: 'DB address value hit',
	db_create: 'DB create',
	db_error: 'DB error',
	google_call: 'Google call',
	google_error: 'Google error',
	cache_hits: 'Cache hits',
	cache_misses: 'Cache misses',
	google_avoid_rate: 'Google avoid rate',
	google_call_rate: 'Google call rate',
	autocomplete_request_total: 'Autocomplete request total',
	autocomplete_served_by_local: 'Autocomplete served by local',
	autocomplete_served_by_google: 'Autocomplete served by Google',
	autocomplete_served_by_error: 'Autocomplete served by error',
	details_session_request_total: 'Details session request total',
	details_session_served_by_google: 'Details served by Google',
	details_session_served_by_error: 'Details served by error',
	session_completed: 'Session completed',
	local_avoid_google_count: 'Local avoided Google',
	old_textsearch_google_call: 'Old Text Search equivalent',
	new_autocomplete_google_call: 'New Autocomplete calls',
	new_details_google_call: 'New Details calls',
	estimated_old_cost_usd: 'Estimated old cost',
	estimated_new_cost_usd: 'Estimated new cost',
	estimated_saved_usd: 'Estimated saved',
	estimated_saved_rate: 'Saved rate',
}

const REVERSE_METRIC_NOTES = [
	['request_total', 'Tổng số request reverse geocode đã gọi vào API nội bộ.'],
	[
		'served_by_redis',
		'Số request có kết quả cuối cùng được trả từ Redis, không gọi Google.',
	],
	[
		'served_by_db_exact',
		'Số request có kết quả cuối cùng được trả từ DB bằng hash chính xác.',
	],
	[
		'served_by_db_nearby',
		'Số request có kết quả cuối cùng được trả từ DB nearby trong bán kính cache.',
	],
	[
		'served_by_google',
		'Số request có kết quả cuối cùng phải gọi Google Maps API.',
	],
	[
		'served_by_error',
		'Số request kết thúc bằng lỗi trước khi trả được kết quả hợp lệ.',
	],
	[
		'cache_hits',
		'Tổng số request có kết quả cuối cùng từ Redis/DB nên tránh được gọi Google.',
	],
	['google_call', 'Số lần thật sự gọi Google Maps API.'],
	['google_avoid_rate', 'Tỷ lệ request tránh được gọi Google theo served_by.'],
	['google_call_rate', 'Tỷ lệ request phải gọi Google theo served_by.'],
]

const PLACES_METRIC_NOTES = [
	['request_total', 'Tổng số request Places textsearch đã gọi vào API nội bộ.'],
	[
		'served_by_redis',
		'Số request Places có kết quả cuối cùng được trả từ Redis, không gọi Google.',
	],
	[
		'served_by_db',
		'Số request Places có kết quả cuối cùng được trả từ DB cache, không gọi Google.',
	],
	[
		'served_by_google',
		'Số request Places có kết quả cuối cùng phải gọi Google Places API.',
	],
	[
		'served_by_error',
		'Số request Places kết thúc bằng lỗi trước khi trả được kết quả hợp lệ.',
	],
	[
		'cache_hits',
		'Tổng số request Places có kết quả cuối cùng từ Redis/DB nên tránh được gọi Google.',
	],
	['google_call', 'Số lần Places textsearch thật sự gọi Google.'],
	['google_avoid_rate', 'Tỷ lệ request Places tránh được gọi Google.'],
	['google_call_rate', 'Tỷ lệ request Places phải gọi Google.'],
]

const AUTOCOMPLETE_METRIC_NOTES = [
	[
		'old_textsearch_google_call',
		'Số call ước tính nếu flow mới vẫn dùng Text Search như cũ; khi chưa có flow mới thì fallback về Text Search actual.',
	],
	[
		'new_autocomplete_google_call',
		'Số Google Autocomplete New call thật sự ở flow mới.',
	],
	[
		'new_details_google_call',
		'Số Place Details Essentials call dùng để kết thúc session.',
	],
	[
		'estimated_old_cost_usd',
		'Ước tính chi phí nếu vẫn dùng Text Search Legacy.',
	],
	[
		'estimated_new_cost_usd',
		'Ước tính chi phí với Autocomplete New + Details Essentials.',
	],
	['estimated_saved_usd', 'Số tiền tiết kiệm ước tính.'],
	['estimated_saved_rate', 'Tỷ lệ tiết kiệm ước tính.'],
	[
		'local_avoid_google_count',
		'Số request trả đủ từ local suggestions nên không cần gọi Google.',
	],
]

const REVERSE_SUMMARY_FIELDS = [
	'request_total',
	'cache_hits',
	'google_call',
	'served_by_redis',
	'served_by_db_exact',
	'served_by_db_nearby',
	'google_avoid_rate',
	'google_call_rate',
]

const PLACES_SUMMARY_FIELDS = [
	'request_total',
	'cache_hits',
	'google_call',
	'served_by_redis',
	'served_by_db',
	'google_avoid_rate',
	'google_call_rate',
]

const AUTOCOMPLETE_SUMMARY_FIELDS = [
	'old_textsearch_google_call',
	'new_autocomplete_google_call',
	'new_details_google_call',
	'estimated_old_cost_usd',
	'estimated_new_cost_usd',
	'estimated_saved_usd',
	'estimated_saved_rate',
	'local_avoid_google_count',
]

const REVERSE_TABLE_FIELDS = [
	'request_total',
	'served_by_redis',
	'served_by_db_exact',
	'served_by_db_nearby',
	'served_by_google',
	'served_by_error',
	'cache_hits',
	'google_call',
	'google_avoid_rate',
	'google_call_rate',
]

const PLACES_TABLE_FIELDS = [
	'request_total',
	'served_by_redis',
	'served_by_db',
	'served_by_google',
	'served_by_error',
	'cache_hits',
	'google_call',
	'google_avoid_rate',
	'google_call_rate',
]

const AUTOCOMPLETE_TABLE_FIELDS = [
	'old_textsearch_google_call',
	'new_autocomplete_google_call',
	'new_details_google_call',
	'autocomplete_served_by_local',
	'session_completed',
	'estimated_old_cost_usd',
	'estimated_new_cost_usd',
	'estimated_saved_usd',
	'estimated_saved_rate',
]

function formatMetricValue(key: string, value: any) {
	const numberValue = Number(value || 0)
	if (RATE_FIELDS.includes(key)) {
		return `${(numberValue * 100).toFixed(2)}%`
	}
	if (MONEY_FIELDS.includes(key)) {
		return `$${numberValue.toFixed(2)}`
	}

	return numberValue.toLocaleString('en-US')
}

function formatStatsDate(date: string) {
	if (!date) return ''
	const parsedDate = dayjs(date, 'YYYYMMDD')
	return parsedDate.isValid() ? parsedDate.format(formatDate) : date
}

function buildStatsRows(stats: any) {
	const daily = Array.isArray(stats.daily)
		? stats.daily
		: stats.daily
			? [stats.daily]
			: []

	return daily.map((item, index) => ({
		...item,
		no: index + 1,
		date_display: formatStatsDate(item.date),
	}))
}

function buildColumns(fields: string[]) {
	return [
		{
			accessorKey: 'no',
			header: 'No',
			size: size.zero,
		},
		{
			accessorKey: 'date_display',
			header: 'Date',
			size: size.text,
		},
		...fields.map((key) => ({
			accessorKey: key,
			header: METRIC_LABELS[key],
			size: RATE_FIELDS.includes(key) ? size.text : size.zero,
			Cell: ({ cell }: any) => formatMetricValue(key, cell.getValue()),
		})),
	]
}

function StatsSection({
	title,
	subtitle,
	notesTitle,
	notes,
	selectedDate,
	allTimeStats,
	dailyStats,
	summaryFields,
	tableFields,
	loading,
}: {
	title: string
	subtitle: string
	notesTitle: string
	notes: string[][]
	selectedDate: Date | null
	allTimeStats: Record<string, any>
	dailyStats: any[]
	summaryFields: string[]
	tableFields: string[]
	loading: boolean
}) {
	const columns = useMemo<MRT_ColumnDef[]>(
		() => buildColumns(tableFields),
		[tableFields],
	)

	return (
		<Stack spacing="md">
			<Stack spacing={4}>
				<Text fw={700} size="md">
					{title}
				</Text>
				<Text color="dimmed" size="sm">
					{subtitle}
				</Text>
			</Stack>
			<Flex gap={16} align="flex-start" wrap="wrap">
				<Stack sx={{ flex: 1, minWidth: 0 }}>
					<SimpleGrid
						cols={summaryFields.length}
						spacing="md"
						breakpoints={[
							{ maxWidth: 1200, cols: 3 },
							{ maxWidth: 768, cols: 2 },
							{ maxWidth: 520, cols: 1 },
						]}
					>
						{summaryFields.map((key) => (
							<Card key={key} withBorder radius="md" p="md">
								<Stack spacing={4}>
									<Text size="sm" color="dimmed">
										{METRIC_LABELS[key]}
									</Text>
									<Title order={3}>
										{formatMetricValue(key, allTimeStats[key])}
									</Title>
								</Stack>
							</Card>
						))}
					</SimpleGrid>
					<Stack sx={{ minWidth: 0 }}>
						<Group position="apart">
							<Text fw={700}>
								{selectedDate
									? 'Daily stats'
									: 'Daily stats - last 30 days'}
							</Text>
							<Badge color="green" variant="light">
								All time summary
							</Badge>
						</Group>
						<TableComponent
							columns={columns}
							total={dailyStats.length}
							data={dailyStats}
							pagination={{
								pageIndex: 1,
								pageSize: dailyStats.length || 10,
							}}
							loading={loading}
							enableRowActions={false}
							handleChangePage={() => {}}
						/>
					</Stack>
				</Stack>

				<Stack sx={{ width: 380, maxWidth: '100%' }}>
					<MetricNotes title={notesTitle} notes={notes} />
				</Stack>
			</Flex>
		</Stack>
	)
}

function MetricNotes({
	title,
	notes,
}: {
	title: string
	notes: string[][]
}) {
	return (
		<Card withBorder radius="md" p="md">
			<Stack spacing="sm">
				<Text fw={700} color={color.CmsPrimary}>
					{title}
				</Text>
				{notes.map(([key, note]) => (
					<Group key={key} spacing={8} align="flex-start" noWrap>
						<Badge variant="light" color="green" sx={{ flexShrink: 0 }}>
							{key}
						</Badge>
						<CustomTooltip label={note} maxWidth={size.medium} />
					</Group>
				))}
			</Stack>
		</Card>
	)
}

function GoogleApiStatsPage() {
	const { openError } = useModal()
	const [loading, setLoading] = useState(false)
	const [selectedDate, setSelectedDate] = useState<Date | null>(null)
	const [reverseDailyStats, setReverseDailyStats] = useState<any[]>([])
	const [reverseAllTimeStats, setReverseAllTimeStats] = useState<
		Record<string, any>
	>({})
	const [placesDailyStats, setPlacesDailyStats] = useState<any[]>([])
	const [placesAllTimeStats, setPlacesAllTimeStats] = useState<
		Record<string, any>
	>({})
	const [autocompleteDailyStats, setAutocompleteDailyStats] = useState<
		any[]
	>([])
	const [
		autocompleteAllTimeStats,
		setAutocompleteAllTimeStats,
	] = useState<Record<string, any>>({})

	const handleGetStats = useCallback(async () => {
		setLoading(true)
		try {
			const queryDate = selectedDate
				? dayjs(selectedDate).format('YYYY-MM-DD')
				: undefined
			const [reverseRes, placesRes, autocompleteRes] = await Promise.all([
				getGoogleGeocodeStats(queryDate),
				getGooglePlacesTextSearchStats(queryDate),
				getGooglePlacesAutocompleteStats(queryDate),
			])
			const reverseObject = reverseRes?.data?.results?.object || {}
			const placesObject = placesRes?.data?.results?.object || {}
			const autocompleteObject =
				autocompleteRes?.data?.results?.object || {}

			setReverseAllTimeStats(reverseObject.all_time || {})
			setReverseDailyStats(buildStatsRows(reverseObject))
			setPlacesAllTimeStats(placesObject.all_time || {})
			setPlacesDailyStats(buildStatsRows(placesObject))
			setAutocompleteAllTimeStats(autocompleteObject.all_time || {})
			setAutocompleteDailyStats(buildStatsRows(autocompleteObject))
		} catch (error) {
			openError(error)
		} finally {
			setLoading(false)
		}
	}, [openError, selectedDate])

	useEffect(() => {
		handleGetStats()
	}, [handleGetStats])

	return (
		<Stack sx={{ width: '100%' }} spacing="md">
			<Flex
				justify="space-between"
				align="flex-end"
				gap={16}
				wrap="wrap"
			>
				<Stack spacing={4}>
					<Text fw={700} size="lg">
						Google API Stats
					</Text>
					<Text color="dimmed" size="sm">
						Reverse geocode and Places textsearch cache stats
					</Text>
				</Stack>
				<Flex gap={12} align="flex-end" wrap="wrap">
					<CustomDatePickerInput
						clearable
						label="Date"
						placeholder="DD/MM/YYYY"
						valueFormat={formatDate}
						value={selectedDate}
						maxDate={new Date()}
						onChange={setSelectedDate}
						sx={{ width: 220 }}
					/>
					<Button
						variant="outline"
						leftIcon={<IconRefresh size={16} />}
						onClick={handleGetStats}
						loading={loading}
					>
						Refresh
					</Button>
				</Flex>
			</Flex>

			<StatsSection
				title="Reverse Geocode"
				subtitle="redis | db_exact | db_nearby | google | error"
				notesTitle="Ghi chú Reverse Geocode"
				notes={REVERSE_METRIC_NOTES}
				selectedDate={selectedDate}
				allTimeStats={reverseAllTimeStats}
				dailyStats={reverseDailyStats}
				summaryFields={REVERSE_SUMMARY_FIELDS}
				tableFields={REVERSE_TABLE_FIELDS}
				loading={loading}
			/>

			<StatsSection
				title="Places Text Search"
				subtitle="redis | db | google | error"
				notesTitle="Ghi chú Places Text Search"
				notes={PLACES_METRIC_NOTES}
				selectedDate={selectedDate}
				allTimeStats={placesAllTimeStats}
				dailyStats={placesDailyStats}
				summaryFields={PLACES_SUMMARY_FIELDS}
				tableFields={PLACES_TABLE_FIELDS}
				loading={loading}
			/>

			<StatsSection
				title="Places Autocomplete Optimization"
				subtitle="old textsearch baseline | autocomplete | details | estimated saving"
				notesTitle="Ghi chú Autocomplete Saving"
				notes={AUTOCOMPLETE_METRIC_NOTES}
				selectedDate={selectedDate}
				allTimeStats={autocompleteAllTimeStats}
				dailyStats={autocompleteDailyStats}
				summaryFields={AUTOCOMPLETE_SUMMARY_FIELDS}
				tableFields={AUTOCOMPLETE_TABLE_FIELDS}
				loading={loading}
			/>
		</Stack>
	)
}

export default React.memo(GoogleApiStatsPage)
